import { useState, useEffect, useRef } from "react";
import { Input } from "./ui/input";
import { Search, Building2, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SchoolLookup = ({ onSelect, darkMode = false, initialIco = "", initialName = "" }) => {
  const [query, setQuery] = useState(initialIco || initialName || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selected, setSelected] = useState(initialIco ? true : false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowResults(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const search = async (val) => {
    if (val.length < 3) { setResults([]); return; }
    setLoading(true);
    try {
      // If it looks like IČO (digits only), lookup directly
      if (/^\d{6,8}$/.test(val)) {
        const res = await axios.get(`${API}/ares-lookup/${val}`);
        if (res.data.ico) {
          setResults([res.data]);
        } else {
          setResults([]);
        }
      } else {
        // Search by name
        const res = await axios.post(`${API}/ares-search`, { query: val });
        setResults(res.data.results || []);
      }
      setShowResults(true);
    } catch (e) { setResults([]); }
    finally { setLoading(false); }
  };

  const handleInput = (val) => {
    setQuery(val);
    setSelected(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 400);
  };

  const handleSelect = (item) => {
    setQuery(`${item.name} (IČO: ${item.ico})`);
    setSelected(true);
    setShowResults(false);
    onSelect({ ico: item.ico, school_name: item.name, address: item.address });
  };

  const baseInput = darkMode
    ? "bg-white/10 border-white/10 text-white placeholder:text-blue-200/30 rounded-xl py-5 focus:border-[#FFC72C]/50 focus:ring-[#FFC72C]/20"
    : "rounded-xl border-[#E8EDF5] py-5 focus:border-[#0049B3] focus:ring-[#0049B3]/20";

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Input
          placeholder="Zadejte IČO nebo název školy..."
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => { if (results.length > 0 && !selected) setShowResults(true); }}
          className={`${baseInput} pr-10`}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {loading ? <Loader2 className={`w-4 h-4 animate-spin ${darkMode ? "text-blue-200/40" : "text-[#9CA3AF]"}`} /> : <Search className={`w-4 h-4 ${darkMode ? "text-blue-200/30" : "text-[#9CA3AF]"}`} />}
        </div>
      </div>

      {showResults && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl border border-[#E8EDF5] shadow-2xl shadow-blue-900/10 max-h-[250px] overflow-y-auto">
          {results.map((item, i) => (
            <button
              key={`${item.ico}-${i}`}
              type="button"
              onClick={() => handleSelect(item)}
              className="w-full text-left px-4 py-3 hover:bg-[#F8FAFF] transition-colors border-b border-[#F0F2F5] last:border-b-0"
            >
              <div className="flex items-start gap-3">
                <Building2 className="w-4 h-4 text-[#0049B3] mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-[#0F1B3D]">{item.name}</div>
                  <div className="text-xs text-[#6B7A99] mt-0.5">IČO: {item.ico}{item.address ? ` · ${item.address}` : ""}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {showResults && results.length === 0 && query.length >= 3 && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl border border-[#E8EDF5] shadow-lg px-4 py-3">
          <p className="text-sm text-[#9CA3AF]">Nic nenalezeno. Zkuste jiný název nebo IČO.</p>
        </div>
      )}
    </div>
  );
};
